import React, { useState } from 'react';
import axios from 'axios';
import { 
  FileBarChart, 
  Download, 
  FileText, 
  FileSpreadsheet,
  File,
  Loader2
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { toast } from 'sonner';
import { API_BASE } from '../../../config/api';

const API = `${API_BASE}/api`;

export default function ReportsStep({ programmeId, programme, token, headers }) {
  const [downloading, setDownloading] = useState(null);

  const downloadReport = async (format) => {
    if (!programmeId) return;
    
    setDownloading(format);
    try {
      if (format === 'json') {
        const res = await axios.get(
          `${API}/programmes/${programmeId}/reports/progress?format=json`,
          { headers }
        );
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        downloadBlob(blob, `progress_report_${programmeId}.json`);
      } else {
        const res = await axios.get(
          `${API}/programmes/${programmeId}/reports/progress?format=${format}`,
          { headers, responseType: 'blob' }
        );
        const filename = format === 'excel' 
          ? `progress_report_${programmeId}.xlsx`
          : `progress_report_${programmeId}.csv`;
        downloadBlob(res.data, filename);
      }
      toast.success(`Report downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to generate report');
    } finally {
      setDownloading(null);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!programmeId) {
    return (
      <div className="text-center py-12">
        <FileBarChart className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">Save Programme First</h3>
        <p className="text-zinc-400">Complete Step 1 to generate reports</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="p-4 bg-orange-500/10 rounded-lg border border-orange-500/30">
        <p className="text-sm text-orange-300">
          Generate comprehensive progress reports showing learner advancement, completion rates, 
          grades, and at-risk status. Download in PDF, Excel, or CSV format.
        </p>
      </div>

      <div className="text-center py-4">
        <FileBarChart className="h-12 w-12 text-orange-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Progress Reports</h3>
        <p className="text-zinc-400 mb-6">
          Programme: <span className="text-white">{programme?.programme_name || programmeId}</span>
        </p>
      </div>

      {/* Report Format Options */}
      <div className="grid grid-cols-3 gap-4">
        {/* PDF Report */}
        <Card className="bg-zinc-800/50 border-zinc-700 hover:border-red-500/50 transition-colors cursor-pointer">
          <CardContent className="p-6 text-center">
            <div className="p-3 rounded-lg bg-red-500/20 w-fit mx-auto mb-4">
              <FileText className="h-8 w-8 text-red-400" />
            </div>
            <h4 className="font-semibold text-white mb-2">PDF Report</h4>
            <p className="text-sm text-zinc-400 mb-4">
              Printable document with formatted tables and charts
            </p>
            <Button
              variant="outline"
              onClick={() => downloadReport('pdf')}
              disabled={downloading === 'pdf'}
              className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              {downloading === 'pdf' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download PDF
            </Button>
          </CardContent>
        </Card>

        {/* Excel Report */}
        <Card className="bg-zinc-800/50 border-zinc-700 hover:border-green-500/50 transition-colors cursor-pointer">
          <CardContent className="p-6 text-center">
            <div className="p-3 rounded-lg bg-green-500/20 w-fit mx-auto mb-4">
              <FileSpreadsheet className="h-8 w-8 text-green-400" />
            </div>
            <h4 className="font-semibold text-white mb-2">Excel Report</h4>
            <p className="text-sm text-zinc-400 mb-4">
              Spreadsheet with all data for analysis and filtering
            </p>
            <Button
              variant="outline"
              onClick={() => downloadReport('excel')}
              disabled={downloading === 'excel'}
              className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              {downloading === 'excel' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download Excel
            </Button>
          </CardContent>
        </Card>

        {/* CSV Report */}
        <Card className="bg-zinc-800/50 border-zinc-700 hover:border-blue-500/50 transition-colors cursor-pointer">
          <CardContent className="p-6 text-center">
            <div className="p-3 rounded-lg bg-blue-500/20 w-fit mx-auto mb-4">
              <File className="h-8 w-8 text-blue-400" />
            </div>
            <h4 className="font-semibold text-white mb-2">CSV Report</h4>
            <p className="text-sm text-zinc-400 mb-4">
              Raw data export compatible with any system
            </p>
            <Button
              variant="outline"
              onClick={() => downloadReport('csv')}
              disabled={downloading === 'csv'}
              className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              {downloading === 'csv' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download CSV
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Report Contents Info */}
      <Card className="bg-zinc-800/30 border-zinc-700">
        <CardContent className="p-4">
          <h4 className="font-medium text-white mb-3">Report includes:</h4>
          <ul className="grid grid-cols-2 gap-2 text-sm text-zinc-400">
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
              Learner names and email addresses
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
              Department and employee ID
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
              Progress percentage
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
              Completed activities count
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
              Overall grades
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
              At-risk status
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
              Enrollment dates
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
              Last activity timestamps
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
